/**
 * Usage:
 *
 * <Lazy className="uitk-grid">
 *   <Lazy.Placeholder>
 *     <QueuedImage />
 *   </Lazy.Placeholder>
 *   <Lazy.Loaded>
 *     <QueuedImage src="/img/url" />
 *   </Lazy.Loaded>
 * </Lazy>
 *
 * Lazy props.once => always toggle between placeholder and loaded as the component go in and out of view.
 */

import * as React from 'react';
import * as invariant from 'invariant';

import * as viewportListener from './viewport-listener';

export const Placeholder = props => props.children;

export const Loaded = props => props.children;

export interface LazyProps {
  className?: string;
  once?: boolean;
  loadOffset?: {};
  parentSelector?: string; // querySelector for parent scroll || window = default
}

export interface LazyState {
  seen: boolean;
}

export class Lazy extends React.PureComponent<LazyProps, LazyState> {
  private el;

  public static defaultProps = {
    once: true,
    parentSelector: 'window',
    loadOffset: { top: 0, bottom: 0 }
  };

  public static Placeholder = Placeholder;
  public static Loaded = Loaded;

  constructor(props) {
    super(props);

    this.state = { seen: false };
    this.observeElement = this.observeElement.bind(this);
  }

  private observeElement(el) {
    if (this.el) {
      viewportListener.unsubscribe(this.el, this.props.parentSelector);
    }

    if (el) {
      const subscribeSuccess = viewportListener.subscribe(
        this.props.parentSelector,
        el,
        {
          didEnter: () => this.componentDidEnterViewport(),
          didExit: () => this.componentDidExitViewport()
        },
        { offset: this.props.loadOffset }
      );

      // error occured, we don't rely on lazy load
      if (!subscribeSuccess) {
        this.componentDidEnterViewport();

        return;
      }
    }

    this.el = el;
  }

  private componentDidEnterViewport() {
    if (this.props.once && this.el) {
      viewportListener.unsubscribe(this.el, this.props.parentSelector);
    }

    this.setState({ seen: true });
  }

  private componentDidExitViewport() {
    this.setState({ seen: false });
  }

  public componentWillUnmount() {
    if (this.el) {
      viewportListener.unsubscribe(this.el, this.props.parentSelector);
      this.el = undefined;
    }
  }

  public render() {
    let placeholder;
    let loaded;

    React.Children.forEach(this.props.children, child => {
      if (React.isValidElement(child)) {
        invariant(
          child.type === Placeholder || child.type === Loaded,
          `Lazy: child type "${(child.type && !(typeof child.type === 'string') && child.type.name) || child.type}" is invalid.
          Allowed types: Placeholder, Loaded]`
        );

        if (child.type === Loaded) {
          loaded = child;
        } else if (child.type === Placeholder) {
          placeholder = child;
        }
      }
    });

    const { seen } = this.state;
    const { className } = this.props;

    return (
      <span ref={this.observeElement} className={className}>
        {seen ? loaded : placeholder}
      </span>
    );
  }
}
